import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  Layers,
  ListChecks,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Shield,
  Trash2,
  Truck,
} from 'lucide-react'
import Button from '../components/forms/Button'
import Modal from '../components/Modal'
import { useToast } from '../contexts/ToastContext'
import { useTheme } from '../contexts/ThemeContext'
import {
  MASTER_CATEGORIES,
  MASTER_MODULES,
  type MasterCategoryId,
  type MasterModuleDef,
} from '../data/mastersCatalog'
import {
  addMasterOption,
  deleteMasterOption,
  fetchAllMasterOptions,
  isApiBackedMasterKey,
  MASTERS_UPDATED_EVENT,
  masterRowKey,
  resetMastersToDefaults,
  updateMasterOption,
  type MasterKey,
  type MasterOption,
} from '../services/mastersService'

const CATEGORY_ICONS: Record<MasterCategoryId, typeof Database> = {
  core: Database,
  'payor-contract': FileText,
  'plans-policies': Layers,
  'claims-rules': ListChecks,
  'fee-rentals': Truck,
  'ownership-transfer': RotateCcw,
  'prior-auth': Shield,
}

type CategoryStyle = {
  headerExpanded: string
  headerCollapsed: string
  headerHover: string
  icon: string
  chevron: string
  badge: string
  badgeActive: string
  panel: string
  itemSelected: string
  itemHover: string
  itemChevron: string
}

const CATEGORY_STYLES: Record<MasterCategoryId, CategoryStyle> = {
  core: {
    headerExpanded: 'bg-indigo-50 text-indigo-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-indigo-50/80',
    icon: 'text-indigo-500',
    chevron: 'text-indigo-400',
    badge: 'bg-indigo-100/80 text-indigo-600',
    badgeActive: 'bg-indigo-200 text-indigo-800',
    panel: 'bg-indigo-50/35 border-l-2 border-indigo-300',
    itemSelected: 'bg-indigo-100 text-indigo-900 font-medium',
    itemHover: 'hover:bg-indigo-50/90 text-slate-700',
    itemChevron: 'text-indigo-500',
  },
  'payor-contract': {
    headerExpanded: 'bg-sky-50 text-sky-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-sky-50/80',
    icon: 'text-sky-500',
    chevron: 'text-sky-400',
    badge: 'bg-sky-100/80 text-sky-600',
    badgeActive: 'bg-sky-200 text-sky-800',
    panel: 'bg-sky-50/35 border-l-2 border-sky-300',
    itemSelected: 'bg-sky-100 text-sky-900 font-medium',
    itemHover: 'hover:bg-sky-50/90 text-slate-700',
    itemChevron: 'text-sky-500',
  },
  'plans-policies': {
    headerExpanded: 'bg-violet-50 text-violet-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-violet-50/80',
    icon: 'text-violet-500',
    chevron: 'text-violet-400',
    badge: 'bg-violet-100/80 text-violet-600',
    badgeActive: 'bg-violet-200 text-violet-800',
    panel: 'bg-violet-50/35 border-l-2 border-violet-300',
    itemSelected: 'bg-violet-100 text-violet-900 font-medium',
    itemHover: 'hover:bg-violet-50/90 text-slate-700',
    itemChevron: 'text-violet-500',
  },
  'claims-rules': {
    headerExpanded: 'bg-amber-50 text-amber-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-amber-50/80',
    icon: 'text-amber-600',
    chevron: 'text-amber-500',
    badge: 'bg-amber-100/80 text-amber-700',
    badgeActive: 'bg-amber-200 text-amber-900',
    panel: 'bg-amber-50/35 border-l-2 border-amber-300',
    itemSelected: 'bg-amber-100 text-amber-900 font-medium',
    itemHover: 'hover:bg-amber-50/90 text-slate-700',
    itemChevron: 'text-amber-600',
  },
  'fee-rentals': {
    headerExpanded: 'bg-emerald-50 text-emerald-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-emerald-50/80',
    icon: 'text-emerald-600',
    chevron: 'text-emerald-500',
    badge: 'bg-emerald-100/80 text-emerald-700',
    badgeActive: 'bg-emerald-200 text-emerald-900',
    panel: 'bg-emerald-50/35 border-l-2 border-emerald-300',
    itemSelected: 'bg-emerald-100 text-emerald-900 font-medium',
    itemHover: 'hover:bg-emerald-50/90 text-slate-700',
    itemChevron: 'text-emerald-600',
  },
  'ownership-transfer': {
    headerExpanded: 'bg-rose-50 text-rose-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-rose-50/80',
    icon: 'text-rose-500',
    chevron: 'text-rose-400',
    badge: 'bg-rose-100/80 text-rose-600',
    badgeActive: 'bg-rose-200 text-rose-800',
    panel: 'bg-rose-50/35 border-l-2 border-rose-300',
    itemSelected: 'bg-rose-100 text-rose-900 font-medium',
    itemHover: 'hover:bg-rose-50/90 text-slate-700',
    itemChevron: 'text-rose-500',
  },
  'prior-auth': {
    headerExpanded: 'bg-teal-50 text-teal-950 shadow-sm',
    headerCollapsed: 'bg-white text-slate-700',
    headerHover: 'hover:bg-teal-50/80',
    icon: 'text-teal-600',
    chevron: 'text-teal-500',
    badge: 'bg-teal-100/80 text-teal-700',
    badgeActive: 'bg-teal-200 text-teal-900',
    panel: 'bg-teal-50/35 border-l-2 border-teal-300',
    itemSelected: 'bg-teal-100 text-teal-900 font-medium',
    itemHover: 'hover:bg-teal-50/90 text-slate-700',
    itemChevron: 'text-teal-600',
  },
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getCategoryForKey(key: MasterKey): MasterCategoryId {
  return MASTER_MODULES.find((m) => m.key === key)?.category ?? 'payor-contract'
}

const Masters = () => {
  const { showToast } = useToast()
  const { theme } = useTheme()
  const [selectedKey, setSelectedKey] = useState<MasterKey>('contract-types')
  const [expandedCategories, setExpandedCategories] = useState<Set<MasterCategoryId>>(
    () => new Set([getCategoryForKey('contract-types')])
  )
  const [rows, setRows] = useState<MasterOption[]>([])
  const [loading, setLoading] = useState(true)
  const [sidebarSearch, setSidebarSearch] = useState('')
  const [valueSearch, setValueSearch] = useState('')
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null)
  const [editingValue, setEditingValue] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [value, setValue] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<MasterOption | null>(null)
  const [resetConfirm, setResetConfirm] = useState(false)

  const selectedModule = useMemo(
    () => MASTER_MODULES.find((m) => m.key === selectedKey) ?? MASTER_MODULES[0],
    [selectedKey]
  )

  const useApi = isApiBackedMasterKey(selectedKey)

  const loadRows = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchAllMasterOptions(selectedKey, {
        useApi,
        q: useApi ? valueSearch : undefined,
      })
      setRows(data)
    } catch {
      showToast('Unable to load master values.', { type: 'error' })
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [selectedKey, showToast, useApi, valueSearch])

  useEffect(() => {
    void loadRows()
  }, [loadRows])

  useEffect(() => {
    if (useApi) return
    const onUpdated = () => {
      void loadRows()
    }
    window.addEventListener(MASTERS_UPDATED_EVENT, onUpdated)
    return () => window.removeEventListener(MASTERS_UPDATED_EVENT, onUpdated)
  }, [loadRows, useApi])

  const filteredSidebarModules = useMemo(() => {
    const q = sidebarSearch.trim().toLowerCase()
    if (!q) return MASTER_MODULES
    return MASTER_MODULES.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q) ||
        m.key.toLowerCase().includes(q)
    )
  }, [sidebarSearch])

  const isSearching = sidebarSearch.trim().length > 0

  const expandedForSidebar = useMemo(() => {
    if (!isSearching) return expandedCategories
    const next = new Set<MasterCategoryId>()
    for (const module of filteredSidebarModules) {
      next.add(module.category)
    }
    return next
  }, [expandedCategories, filteredSidebarModules, isSearching])

  const toggleCategory = (categoryId: MasterCategoryId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  const selectModule = (key: MasterKey) => {
    setSelectedKey(key)
    setValueSearch('')
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      next.add(getCategoryForKey(key))
      return next
    })
  }

  const filteredRows = useMemo(() => {
    if (useApi) return rows
    const q = valueSearch.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (r) => r.label.toLowerCase().includes(q) || r.value.toLowerCase().includes(q)
    )
  }, [rows, valueSearch, useApi])

  const activeCount = rows.filter((r) => r.isActive !== false).length

  const previewOptions = useMemo(
    () => rows.filter((r) => r.isActive !== false),
    [rows]
  )

  const openAddModal = () => {
    setModalMode('add')
    setEditingValue(null)
    setLabel('')
    setValue('')
  }

  const openEditModal = (row: MasterOption) => {
    setModalMode('edit')
    setEditingValue(masterRowKey(row))
    setLabel(row.label)
    setValue(row.value)
  }

  const closeModal = () => {
    setModalMode(null)
    setEditingValue(null)
    setLabel('')
    setValue('')
  }

  const handleSave = async () => {
    const nextLabel = label.trim()
    const nextValue = (value.trim() || slugify(nextLabel)).trim()
    if (!nextLabel || !nextValue) {
      showToast('Label and value are required.', { type: 'error' })
      return
    }

    const duplicate = rows.some(
      (r) =>
        (modalMode !== 'edit' || masterRowKey(r) !== editingValue) &&
        (r.label.toLowerCase() === nextLabel.toLowerCase() ||
          r.value.toLowerCase() === nextValue.toLowerCase())
    )
    if (modalMode === 'add' && duplicate) {
      showToast('A value with this label or key already exists.', { type: 'error' })
      return
    }

    try {
      if (modalMode === 'edit' && editingValue) {
        const currentRow = rows.find((r) => masterRowKey(r) === editingValue)
        await updateMasterOption(
          selectedKey,
          editingValue,
          {
            label: nextLabel,
            value: nextValue,
            isActive: currentRow?.isActive !== false,
          },
          { useApi }
        )
        showToast('Master value updated.', { type: 'success' })
      } else {
        await addMasterOption(
          selectedKey,
          {
            label: nextLabel,
            value: nextValue,
            isActive: true,
          },
          { useApi }
        )
        showToast('Master value added.', { type: 'success' })
      }
      closeModal()
      await loadRows()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Unable to save.', { type: 'error' })
    }
  }

  const handleToggle = async (row: MasterOption) => {
    const nextActive = row.isActive === false
    try {
      await updateMasterOption(
        selectedKey,
        masterRowKey(row),
        {
          label: row.label,
          value: row.value,
          isActive: nextActive,
        },
        { useApi }
      )
      await loadRows()
    } catch {
      showToast('Unable to update status.', { type: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteMasterOption(selectedKey, masterRowKey(deleteConfirm), { useApi })
      showToast('Master value removed.', { type: 'success' })
      setDeleteConfirm(null)
      await loadRows()
    } catch {
      showToast('Unable to delete value.', { type: 'error' })
    }
  }

  const handleReset = () => {
    resetMastersToDefaults(selectedKey)
    showToast(`${selectedModule.name} reset to defaults.`, { type: 'info' })
    setResetConfirm(false)
    void loadRows()
  }

  const cardStyle = {
    backgroundColor: theme.colors.cardBackground,
    border: `1px solid ${theme.colors.cardBorder}`,
  }

  const inputStyle = {
    backgroundColor: theme.colors.inputBackground,
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textPrimary,
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex items-start gap-3">
          <div
            className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: theme.colors.primaryLight, color: theme.colors.primary }}
          >
            <BookOpen size={22} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
              Masters
            </h1>
            <p className="mt-1 text-sm" style={{ color: theme.colors.textSecondary }}>
              Manage shared dropdown values for payor, claims, rentals, and prior auth — updates apply instantly.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 min-h-[min(72vh,720px)]">
        {/* Sidebar */}
        <aside
          className="w-full lg:w-72 xl:w-80 shrink-0 flex flex-col rounded-xl shadow-sm overflow-hidden"
          style={cardStyle}
        >
          <div className="p-3" style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: theme.colors.textSecondary }}
              />
              <input
                type="search"
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                placeholder="Search masters…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                style={inputStyle}
              />
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {MASTER_CATEGORIES.map((category) => {
              const modules = filteredSidebarModules.filter((m) => m.category === category.id)
              if (modules.length === 0) return null
              const isExpanded = expandedForSidebar.has(category.id)
              const hasSelectedChild = modules.some((m) => m.key === selectedKey)
              return (
                <CategorySection
                  key={category.id}
                  categoryId={category.id}
                  label={category.label}
                  icon={CATEGORY_ICONS[category.id]}
                  modules={modules}
                  isExpanded={isExpanded}
                  hasSelectedChild={hasSelectedChild}
                  selectedKey={selectedKey}
                  onToggle={() => toggleCategory(category.id)}
                  onSelectModule={selectModule}
                />
              )
            })}
          </nav>
        </aside>

        {/* Detail panel */}
        <main className="flex-1 min-w-0 flex flex-col rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
          <div
            className="px-4 sm:px-5 py-4"
            style={{
              borderBottom: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.tableHeaderBackground,
            }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p
                  className="text-[11px] font-semibold uppercase tracking-wider mb-1"
                  style={{ color: theme.colors.primary }}
                >
                  {MASTER_CATEGORIES.find((c) => c.id === selectedModule.category)?.label}
                </p>
                <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                  {selectedModule.name}
                </h2>
                <p className="text-sm mt-0.5" style={{ color: theme.colors.textSecondary }}>
                  {selectedModule.description}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{
                      backgroundColor: theme.colors.tableRowHover,
                      color: theme.colors.textPrimary,
                    }}
                  >
                    {activeCount} active
                  </span>
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{
                      backgroundColor: theme.colors.tableRowHover,
                      color: theme.colors.textSecondary,
                    }}
                  >
                    {rows.length} total
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {!useApi && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setResetConfirm(true)}
                    title="Reset to defaults"
                  >
                    <span className="flex items-center gap-1.5">
                      <RefreshCw size={14} />
                      Reset
                    </span>
                  </Button>
                )}
                <Button type="button" variant="primary" size="sm" onClick={openAddModal}>
                  <span className="flex items-center gap-1.5">
                    <Plus size={16} />
                    Add value
                  </span>
                </Button>
              </div>
            </div>
          </div>

          <div
            className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
            style={{
              borderBottom: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
            }}
          >
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: theme.colors.textSecondary }}
              />
              <input
                type="search"
                value={valueSearch}
                onChange={(e) => setValueSearch(e.target.value)}
                placeholder="Filter values…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                style={inputStyle}
              />
            </div>
            <DropdownPreview module={selectedModule} options={previewOptions} />
          </div>

          <div className="flex-1 overflow-auto">
            {loading ? (
              <div
                className="flex items-center justify-center py-16 text-sm"
                style={{ color: theme.colors.textSecondary }}
              >
                Loading values…
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: theme.colors.tableRowHover }}
                >
                  <ListChecks size={22} style={{ color: theme.colors.textSecondary }} />
                </div>
                <p className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>
                  No values yet
                </p>
                <p className="text-sm mt-1 max-w-sm" style={{ color: theme.colors.textSecondary }}>
                  {valueSearch
                    ? 'No values match your filter.'
                    : `Add options for ${selectedModule.name} to populate dropdowns across the app.`}
                </p>
                {!valueSearch && (
                  <Button type="button" variant="primary" size="sm" className="mt-4" onClick={openAddModal}>
                    <span className="flex items-center gap-1.5">
                      <Plus size={16} />
                      Add first value
                    </span>
                  </Button>
                )}
              </div>
            ) : (
              <table className="w-full min-w-[520px]">
                <thead
                  className="sticky top-0 z-[1] backdrop-blur-sm"
                  style={{
                    backgroundColor: theme.colors.tableHeaderBackground,
                    borderBottom: `1px solid ${theme.colors.tableBorder}`,
                  }}
                >
                  <tr>
                    {['Display label', 'Value key', 'Status', 'Actions'].map((header, index) => (
                      <th
                        key={header}
                        className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide ${
                          index === 0 ? 'text-left w-[40%]' : index === 2 ? 'text-left w-24' : index === 3 ? 'text-right w-28' : 'text-left'
                        }`}
                        style={{ color: theme.colors.tableHeaderText }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={masterRowKey(row)}
                      className={`group transition-colors ${
                        row.isActive === false ? 'opacity-60' : ''
                      }`}
                      style={{
                        borderTop: `1px solid ${theme.colors.tableBorder}`,
                        backgroundColor: theme.colors.tableRowBackground,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.tableRowHover
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.tableRowBackground
                      }}
                    >
                      <td
                        className="px-4 py-3 text-sm font-medium"
                        style={{ color: theme.colors.textPrimary }}
                      >
                        {row.label}
                      </td>
                      <td className="px-4 py-3">
                        <code
                          className="text-xs font-mono px-1.5 py-0.5 rounded"
                          style={{
                            color: theme.colors.textSecondary,
                            backgroundColor: theme.colors.tableRowHover,
                          }}
                        >
                          {row.value}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => void handleToggle(row)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                            row.isActive !== false
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              row.isActive !== false ? 'bg-green-500' : 'bg-gray-400'
                            }`}
                          />
                          {row.isActive !== false ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1 opacity-80 group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            aria-label={`Edit ${row.label}`}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(row)}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                            aria-label={`Delete ${row.label}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>

      {/* Add / Edit modal */}
      <Modal
        isOpen={modalMode !== null}
        onClose={closeModal}
        title={modalMode === 'edit' ? `Edit ${selectedModule.name}` : `Add ${selectedModule.name}`}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {modalMode === 'edit'
              ? 'Update the display label or value key. Active dropdowns will reflect changes immediately.'
              : 'New values appear in dropdowns across payor configuration and related screens.'}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Display label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              placeholder="e.g. In-network PPO"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Value key</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              placeholder="Auto-generated from label if left blank"
            />
            <p className="mt-1 text-xs text-gray-500">
              Stored value sent to APIs and used in form state.
            </p>
          </div>
          <div className="pt-2 border-t border-gray-200 flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={() => void handleSave()} disabled={!label.trim()}>
              {modalMode === 'edit' ? 'Save changes' : 'Add value'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        title="Remove master value?"
        size="sm"
      >
        <p className="text-sm text-gray-600">
          Remove <strong>{deleteConfirm?.label}</strong> from {selectedModule.name}? This cannot be
          undone, but you can add it again later.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={() => void handleDelete()}>
            Remove
          </Button>
        </div>
      </Modal>

      {/* Reset confirm */}
      <Modal
        isOpen={resetConfirm}
        onClose={() => setResetConfirm(false)}
        title={`Reset ${selectedModule.name}?`}
        size="sm"
      >
        <p className="text-sm text-gray-600">
          Restore default values for {selectedModule.name}? Custom additions will be removed.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setResetConfirm(false)}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={handleReset}>
            Reset to defaults
          </Button>
        </div>
      </Modal>
    </div>
  )
}

function CategorySection({
  categoryId,
  label,
  icon: Icon,
  modules,
  isExpanded,
  hasSelectedChild,
  selectedKey,
  onToggle,
  onSelectModule,
}: {
  categoryId: MasterCategoryId
  label: string
  icon: typeof Database
  modules: MasterModuleDef[]
  isExpanded: boolean
  hasSelectedChild: boolean
  selectedKey: MasterKey
  onToggle: () => void
  onSelectModule: (key: MasterKey) => void
}) {
  const { theme } = useTheme()
  const panelId = `masters-category-${categoryId}`
  const styles = CATEGORY_STYLES[categoryId]

  const headerStyle: CSSProperties = theme.isDarkMode
    ? {
        backgroundColor: isExpanded
          ? theme.colors.primaryLight
          : theme.colors.tableRowBackground,
        color: theme.colors.textPrimary,
      }
    : {}

  const headerClass = theme.isDarkMode
    ? `${styles.headerHover} shadow-sm`
    : isExpanded
      ? styles.headerExpanded
      : hasSelectedChild
        ? `${styles.headerCollapsed} ring-1 ring-inset ring-black/5`
        : styles.headerCollapsed

  return (
    <div
      className="rounded-xl border overflow-hidden transition-shadow"
      style={{ borderColor: theme.colors.border }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className={`w-full flex items-center gap-2 px-2.5 py-2.5 text-left transition-all duration-200 ${headerClass} ${theme.isDarkMode ? '' : styles.headerHover}`}
        style={headerStyle}
      >
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform duration-200 ${styles.chevron} ${
            isExpanded ? 'rotate-0' : '-rotate-90'
          }`}
        />
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{
            backgroundColor: theme.isDarkMode
              ? theme.colors.tableRowHover
              : isExpanded
                ? 'rgba(255,255,255,0.7)'
                : theme.colors.background,
          }}
        >
          <Icon size={14} className={styles.icon} />
        </span>
        <span className="flex-1 text-xs font-semibold uppercase tracking-wider truncate">
          {label}
        </span>
        <span
          className={`shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold inline-flex items-center justify-center ${
            hasSelectedChild ? styles.badgeActive : styles.badge
          }`}
        >
          {modules.length}
        </span>
      </button>
      <div
        id={panelId}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden min-h-0">
          <ul
            className={`space-y-0.5 py-1.5 pl-2 pr-1 ${isExpanded && !theme.isDarkMode ? styles.panel : ''}`}
            style={
              isExpanded && theme.isDarkMode
                ? {
                    backgroundColor: theme.colors.tableRowHover,
                    borderLeft: `2px solid ${theme.colors.primary}`,
                  }
                : undefined
            }
          >
            {modules.map((module) => (
              <SidebarItem
                key={module.key}
                module={module}
                selected={selectedKey === module.key}
                styles={styles}
                onSelect={() => onSelectModule(module.key)}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function SidebarItem({
  module,
  selected,
  styles,
  onSelect,
}: {
  module: MasterModuleDef
  selected: boolean
  styles: CategoryStyle
  onSelect: () => void
}) {
  const { theme } = useTheme()

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`w-full flex items-center gap-2 pl-3 pr-2.5 py-2 rounded-lg text-left text-sm transition-colors ${
          theme.isDarkMode ? '' : selected ? styles.itemSelected : styles.itemHover
        }`}
        style={
          theme.isDarkMode
            ? {
                backgroundColor: selected ? theme.colors.sidebarActiveBackground : 'transparent',
                color: selected ? theme.colors.sidebarActiveText : theme.colors.textSecondary,
                fontWeight: selected ? 500 : 400,
              }
            : undefined
        }
        onMouseEnter={(e) => {
          if (theme.isDarkMode && !selected) {
            e.currentTarget.style.backgroundColor = theme.colors.tableRowHover
          }
        }}
        onMouseLeave={(e) => {
          if (theme.isDarkMode && !selected) {
            e.currentTarget.style.backgroundColor = 'transparent'
          }
        }}
      >
        <span className="truncate flex-1">{module.name}</span>
        {selected && (
          <ChevronRight
            size={14}
            className={`shrink-0 ${theme.isDarkMode ? '' : styles.itemChevron}`}
            style={theme.isDarkMode ? { color: theme.colors.primary } : undefined}
          />
        )}
      </button>
    </li>
  )
}

function DropdownPreview({
  module,
  options,
}: {
  module: MasterModuleDef
  options: MasterOption[]
}) {
  const { theme } = useTheme()

  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-xs hidden sm:inline" style={{ color: theme.colors.textSecondary }}>
        Preview:
      </span>
      <select
        className="min-w-[160px] max-w-[220px] px-3 py-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
        style={{
          backgroundColor: theme.colors.inputBackground,
          border: `1px solid ${theme.colors.border}`,
          color: theme.colors.textPrimary,
        }}
        defaultValue=""
        aria-label={`Preview ${module.name} dropdown`}
      >
        <option value="" disabled>
          {module.placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export default Masters
